/**
 * Formato di trasporto dell'identità CRM unica già usato da Martina
 * (`@JustEat`, `crm:company:1`). Il tag `@` è interno: la UI del composer
 * mostra il nome della scheda, non la chiocciola.
 *
 * Unicità: `classifyCanonicalCrmIdentityCandidates` (chiave `crm:type:id`).
 * Menzione testuale: `classifyCanonicalCrmMentionCandidates` filtra il titolo
 * con match esatto/forte e poi classifica. Composer e orchestratore riusano
 * questo contratto; niente matcher privato.
 */
export function canonicalCrmMentionTag(title: string): string {
  const compact = String(title ?? '').normalize('NFC').replace(/[^\p{L}\p{N}]+/gu, '');
  return compact ? `@${compact}` : '';
}

export function canonicalCrmEntityRef(entityType: string, id: string | number): string | null {
  const type = String(entityType ?? '').trim().toLowerCase();
  const rawId = String(id ?? '').trim();
  if (!/^[a-z_]+$/.test(type) || !rawId) return null;
  return `crm:${type}:${rawId}`;
}

/**
 * Chip già serializzati nel payload del turno (`TITLE @Tag crm:type:id`).
 * Trasporta l'identità canonica esistente: non inventa un secondo soggetto.
 */
const COMPOSER_CHIP_TRANSPORT_PATTERN =
  /([A-ZÀ-ÖØ-Ý][\p{L}'’&.-]*(?:\s+[A-ZÀ-ÖØ-Ý][\p{L}'’&.-]*){0,3})\s+@[\p{L}\p{N}]+\s+crm:([a-z_]+):([A-Za-z0-9_-]+)/gu;

export type CanonicalCrmChipMatch = {
  type: string;
  id: string;
  title: string;
  href: string;
  tag: string;
  entityRef: string;
  start: number;
  end: number;
};

/** Lettura del trasporto persistito: la UI può ricostruire il link CRM. */
export function canonicalCrmChipMatchesFromText(text: string): CanonicalCrmChipMatch[] {
  const matches: CanonicalCrmChipMatch[] = [];
  for (const match of String(text ?? '').matchAll(COMPOSER_CHIP_TRANSPORT_PATTERN)) {
    if (match.index == null) continue;
    const title = String(match[1] ?? '').trim();
    const type = String(match[2] ?? '').trim().toLowerCase();
    const id = String(match[3] ?? '').trim();
    const entityRef = canonicalCrmEntityRef(type, id);
    const href = canonicalCrmRecordHref(type, id);
    const tag = canonicalCrmMentionTag(title);
    if (!title || !entityRef || !href || !tag) continue;
    matches.push({ type, id, title, href, tag, entityRef, start: match.index, end: match.index + match[0].length });
  }
  return matches;
}

export function canonicalCrmChipCandidatesFromText(text: string): CanonicalCrmIdentityCandidate[] {
  return canonicalCrmChipMatchesFromText(text).map(({ type, id, title, href }) => ({ type, id, title, href }));
}

export function canonicalCrmRecordHref(
  entityType: string,
  id: string | number,
  href?: string | null,
): string | null {
  const provided = String(href ?? '').trim();
  if (provided.startsWith('/')) return provided;
  const rawId = String(id ?? '').trim();
  if (!rawId) return null;
  switch (String(entityType ?? '').trim().toLowerCase()) {
    case 'contact':
      return `/contacts/${rawId}`;
    case 'company':
      return `/companies/${rawId}`;
    case 'lead':
      return `/leads/${rawId}`;
    case 'deal':
      return `/deals/${rawId}`;
    case 'branch':
      return `/branches/${rawId}`;
    case 'brand':
      return `/brands?brandId=${rawId}`;
    case 'synergy':
      return `/synergies?synergyId=${rawId}`;
    default:
      return null;
  }
}

export type CanonicalCrmIdentityCandidate = {
  type: string;
  id: string;
  title: string;
  /** Distintivo CRM (azienda, ruolo, …) per omonimia nel picker. */
  subtitle?: string;
  href?: string;
};

export type CanonicalCrmIdentityClassification =
  | { status: 'unique'; candidate: CanonicalCrmIdentityCandidate }
  | { status: 'ambiguous'; candidates: CanonicalCrmIdentityCandidate[] }
  | { status: 'none' };

export type CanonicalCrmIdentityResolution = {
  status: 'none' | 'candidate_set' | 'needs_clarification';
  candidates?: readonly CanonicalCrmIdentityCandidate[] | null;
};

/** Contratto unico: 0 chiavi → none, 1 → unique, 2+ distinte → ambiguous. */
export function classifyCanonicalCrmIdentityCandidates(
  candidates: readonly CanonicalCrmIdentityCandidate[] | null | undefined,
): CanonicalCrmIdentityClassification {
  const byRef = new Map<string, CanonicalCrmIdentityCandidate>();
  for (const candidate of candidates ?? []) {
    const type = String(candidate.type ?? '').trim().toLowerCase();
    const id = String(candidate.id ?? '').trim();
    const title = String(candidate.title ?? '').trim();
    const entityRef = canonicalCrmEntityRef(type, id);
    if (!entityRef || !title) continue;
    if (byRef.has(entityRef)) continue;
    byRef.set(entityRef, {
      type,
      id,
      title,
      ...(candidate.subtitle ? { subtitle: String(candidate.subtitle).trim() || undefined } : {}),
      ...(candidate.href ? { href: candidate.href } : {}),
    });
  }
  const distinct = [...byRef.values()];
  if (distinct.length === 0) return { status: 'none' };
  if (distinct.length > 1) return { status: 'ambiguous', candidates: distinct };
  return { status: 'unique', candidate: distinct[0]! };
}

function compactCanonicalCrmIdentity(value: string): string {
  return String(value ?? '').normalize('NFC').replace(/[^\p{L}\p{N}]+/gu, '').toLocaleLowerCase('it-IT');
}

function canonicalCrmIdentityTokens(value: string): string[] {
  return (String(value ?? '').normalize('NFC').match(/\p{L}[\p{L}\p{N}'’.-]*/gu) ?? [])
    .map((token) => compactCanonicalCrmIdentity(token))
    .filter(Boolean);
}

/** Match esatto (titolo compatto uguale) o forte (tutti i token di un nome 2+). */
export function isStrongCanonicalCrmTitleMatch(query: string, title: string): boolean {
  const queryCompact = compactCanonicalCrmIdentity(query);
  const titleCompact = compactCanonicalCrmIdentity(title);
  if (!queryCompact || !titleCompact) return false;
  if (titleCompact === queryCompact) return true;
  const tokens = canonicalCrmIdentityTokens(query);
  if (tokens.length < 2) return false;
  return tokens.every((token) => titleCompact.includes(token));
}

/** Candidati di una menzione testuale: solo titoli con match esatto/forte. */
export function selectCanonicalCrmMentionCandidates(
  query: string,
  candidates: readonly CanonicalCrmIdentityCandidate[] | null | undefined,
): CanonicalCrmIdentityCandidate[] {
  const queryText = String(query ?? '').trim();
  if (!queryText) return [];
  return (candidates ?? []).filter((candidate) =>
    isStrongCanonicalCrmTitleMatch(queryText, candidate.title),
  );
}

/**
 * Selezione menzione testuale: match forte sul titolo, poi unicità type+id.
 * 0 forti → none; 1 forte → unique; 2+ forti distinti → ambiguous.
 */
export function classifyCanonicalCrmMentionCandidates(
  query: string,
  candidates: readonly CanonicalCrmIdentityCandidate[] | null | undefined,
): CanonicalCrmIdentityClassification {
  return classifyCanonicalCrmIdentityCandidates(
    selectCanonicalCrmMentionCandidates(query, candidates),
  );
}

export type MartinaCanonicalCrmAnchor = {
  tag: string;
  entityRef: string;
  entityType: string;
  id: string;
  title: string;
};

/**
 * Ancora canonica solo da una risoluzione univoca owner-scoped.
 * Ambiguo o assente → null: chiarimento o non verificato, mai un match inventato.
 */
export function resolveCanonicalCrmAnchor(
  resolution: CanonicalCrmIdentityResolution | null | undefined,
): MartinaCanonicalCrmAnchor | null {
  if (!resolution || resolution.status !== 'none') return null;
  const classified = classifyCanonicalCrmIdentityCandidates(resolution.candidates);
  if (classified.status !== 'unique') return null;
  const candidate = classified.candidate;
  const entityRef = canonicalCrmEntityRef(candidate.type, candidate.id);
  const tag = canonicalCrmMentionTag(candidate.title);
  if (!entityRef || !tag) return null;
  return {
    tag,
    entityRef,
    entityType: candidate.type,
    id: candidate.id,
    title: candidate.title,
  };
}
